import React, { useState, useEffect } from 'react';
import Button from '../components/Button';
import Card from '../components/Card';
import NavBar from '../components/NavBar';
import ProgressBar from '../components/ProgressBar';
import Badge from '../components/Badge';
import EducationalBanner from '../components/EducationalBanner';
import { useFirstVisit } from '../hooks/useFirstVisit';

function QuestDashboard() {
  const [xp, setXp] = useState(250);
  const [level] = useState(3);
  const { isFirstVisit, setVisited } = useFirstVisit('quests');
  const [showEducationalBanner, setShowEducationalBanner] = useState(false);
  const [quests, setQuests] = useState([
    { id: 1, title: "Morning Mindfulness", description: "Complete a 3-minute breathing exercise", progress: 100, xpReward: 50, completed: true },
    { id: 2, title: "Nutrition Tracker", description: "Log 3 meals with balanced nutrition", progress: 66, xpReward: 30, completed: false },
    { id: 3, title: "Social Connection", description: "Have a meaningful conversation with a friend", progress: 0, xpReward: 40, completed: false },
    { id: 4, title: "Digital Detox", description: "Take a 2-hour break from social media", progress: 25, xpReward: 35, completed: false }
  ]);
  // Scroll to top on mount and check for first visit
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Check if this is the user's first visit to this feature
    if (isFirstVisit) {
      setShowEducationalBanner(true);
    }
  }, [isFirstVisit]);

  function claimReward(questId) {
    setQuests(prev => prev.map(q => 
      q.id === questId ? { ...q, completed: true, progress: 100 } : q
    ));
    const quest = quests.find(q => q.id === questId);
    setXp(prev => prev + quest.xpReward);
  }

  // Handle closing the educational banner
  const handleCloseBanner = () => {
    setShowEducationalBanner(false);
    setVisited();
  };

  return (
    <div style={{ maxWidth: '480px', margin: 'auto', padding: '20px', backgroundColor: '#f5f7fa', minHeight: '100vh', paddingTop: '60px' }}>
      <NavBar title="🎯 Quest Dashboard" />
      
      {/* Educational Banner - Only shows on first visit */}
      {showEducationalBanner && (
        <EducationalBanner 
          feature="quests" 
          onDismiss={handleCloseBanner} 
        />
      )}
      
      <Card style={{ 
        backgroundColor: 'linear-gradient(135deg, #4361ee 0%, #4cc9f0 100%)', 
        color: 'white', 
        textAlign: 'center',
        backgroundImage: 'linear-gradient(135deg, #4361ee 0%, #4cc9f0 100%)'
      }}>
        <h1 style={{ margin: '0 0 8px 0', fontWeight: '700' }}>🎯 Quest Dashboard</h1>
        <div style={{ fontSize: '18px', fontWeight: '500' }}>
          Level {level} • {xp} XP
        </div>
        <div style={{ margin: '16px 0 8px 0' }}>
          <ProgressBar 
            percentage={xp % 100} 
            color="#ffffff" 
            height="8px" 
            showPercentage={false} 
          />
        </div>
        <div style={{ fontSize: '14px', opacity: 0.9 }}>
          {100 - (xp % 100)} XP to next level
        </div>
      </Card>

      <h2 style={{ color: '#2c3e50', margin: '24px 0 16px 0', fontWeight: '600' }}>Today&apos;s Quests</h2>

      {quests.map(quest => (
        <Card 
          key={quest.id} 
          style={{ 
            opacity: quest.completed ? 0.8 : 1,
            borderLeft: quest.completed ? '4px solid #4ade80' : '4px solid #4361ee',
            transition: 'all 0.3s ease'
          }}
          hoverable
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <h3 style={{ 
              margin: 0, 
              color: quest.completed ? '#4ade80' : '#2c3e50',
              fontWeight: '600'
            }}>
              {quest.completed ? '✅' : '⭐'} {quest.title}
            </h3>
            <Badge variant={quest.completed ? "success" : "primary"}>
              +{quest.xpReward} XP
            </Badge>
          </div>
          
          <p style={{ color: '#64748b', margin: '8px 0 16px 0', fontSize: '14px' }}>
            {quest.description}
          </p>
          
          <div style={{ marginBottom: '16px' }}>
            <ProgressBar 
              percentage={quest.progress} 
              color={quest.completed ? '#4ade80' : '#4361ee'} 
              height="6px" 
              showPercentage={true} 
            />
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>
              {quest.completed ? 'Completed' : `${quest.progress}% Complete`}
            </span>
            {!quest.completed && quest.progress === 100 && (
              <Button 
                onClick={() => claimReward(quest.id)} 
                variant="success"
                size="sm"
              >
                Claim Reward
              </Button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

export default QuestDashboard;
